import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {v4 as uuidv4} from 'uuid';

interface SessionStateDefinition {
  sessionId: string | null;
  isInitialized: boolean;
}

interface SessionContextValue {
  state: SessionStateDefinition;
  setSessionId: (sessionId: string) => void;
  clearSessionId: () => void;
  generateSessionId: () => string;
}

const SESSION_ID_KEY_NAME = 'session_id';

const initialState: SessionStateDefinition = {
  sessionId: null,
  isInitialized: false,
};

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined
);

// Get session ID from sessionStorage
const getSessionIdFromStorage = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_ID_KEY_NAME);
  } catch (error) {
    console.warn('Failed to read session ID from sessionStorage:', error);
    return null;
  }
};

// Set session ID in sessionStorage
const setSessionIdInStorage = (sessionId: string): void => {
  try {
    sessionStorage.setItem(SESSION_ID_KEY_NAME, sessionId);
  } catch (error) {
    console.warn('Failed to write session ID to sessionStorage:', error);
  }
};

// Remove session ID from sessionStorage
const removeSessionIdFromStorage = (): void => {
  try {
    sessionStorage.removeItem(SESSION_ID_KEY_NAME);
  } catch (error) {
    console.warn('Failed to remove session ID from sessionStorage:', error);
  }
};

export const useSession = (): SessionContextValue => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({children}: SessionProviderProps) {
  const [state, setState] = useState<SessionStateDefinition>(initialState);

  // Initialize session ID from sessionStorage on mount
  useEffect(() => {
    const storedSessionId = getSessionIdFromStorage();

    if (storedSessionId) {
      setState({
        sessionId: storedSessionId,
        isInitialized: true,
      });
    } else {
      // Generate a new session ID if none exists
      const newSessionId = uuidv4();
      setSessionIdInStorage(newSessionId);
      setState({
        sessionId: newSessionId,
        isInitialized: true,
      });
    }
  }, []);

  const setSessionId = useCallback((sessionId: string) => {
    setSessionIdInStorage(sessionId);
    setState((prevState) => ({
      ...prevState,
      sessionId,
    }));
  }, []);

  const clearSessionId = useCallback(() => {
    removeSessionIdFromStorage();
    setState((prevState) => ({
      ...prevState,
      sessionId: null,
    }));
  }, []);

  const generateSessionId = useCallback(() => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    return newSessionId;
  }, [setSessionId]);

  const contextValue = useMemo<SessionContextValue>(() => {
    return {
      state,
      setSessionId,
      clearSessionId,
      generateSessionId,
    };
  }, [state, setSessionId, clearSessionId, generateSessionId]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
}
